import React, { useRef, useState } from 'react';
import { Box, Card, Stack, Button, Typography, TextField, Container, Paper } from '@mui/material';
import { UserSignIn } from '../../Redux/Actions/authAction';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SidePic from '../../Images/side_pic.png';
import { useDispatch } from 'react-redux';
import '../../Styles/ForgotPassword.css';
import { GetUserRoles, GetParentsRoles } from '../../Redux/Actions/authAction';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';

const TwoFactorCode = ({ email, password }) => {
  const dispatch = useDispatch();
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState([]);
  
  const formik = useFormik({
    initialValues: {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: '',
    },
    validationSchema: Yup.object({
      code1: Yup.string().required(''),
      code2: Yup.string().required(''),
      code3: Yup.string().required(''),
      code4: Yup.string().required(''),
      code5: Yup.string().required(''),
      code6: Yup.string().required(''),
    }),
    onSubmit: async (values) => {
      const code = Object.values(values).join('');
      const body = {
        token: code,
        email,  
        password, 
        portal: 'MANAGER'
      };
      const response = await dispatch(UserSignIn(body));
      if (response?.payload?.statusCode === 201) {
        const response = await dispatch(GetUserRoles());
        if (response && response?.payload.statusCode === 200) {
          setUserRole(response?.payload?.data);
          localStorage.setItem("kyctype", response?.payload?.data[0]?.investorType);
          localStorage.setItem("type", response?.payload?.data[0]?.role); 
          
          if (Array.isArray(response?.payload?.data) && response.payload.data.length > 0) {
            if (response.payload.data.length === 1 && response.payload.data[0]?.role === "INVESTOR") {
              navigate('/app');
            } else if (response.payload.data.length > 1 && response.payload.data.some(role => role.role === "INVESTOR")) {
              navigate('/selectaccount');
            } else {
              const type = "SUB_INVESTOR"
              
              try {
                const response = await dispatch(GetParentsRoles(type));
                if (response && response?.payload.statusCode === 200) {
                  const data = response?.payload?.data;
                  const allPending = data.every(record => record.status === 'PENDING');
                  
                  if (allPending) {
                    navigate('/accountapproval');
                  } else {
                    navigate('/selectaccount');
                  }
                }
              } catch (error) {
                console.error('Error fetching data:', error);
              }
            }
          }
        }
      } 
    },
  });

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (/^\d$/.test(value)) {
      formik.setFieldValue(e.target.name, value);
      if (index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    } else {
      formik.setFieldValue(e.target.name, '');
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !formik.values[e.target.name] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
          background: 'linear-gradient(to bottom, #f5f7fa, #e4e7eb)',
        }}
      >
        <Container maxWidth="sm">
          <Paper 
            elevation={6} 
            sx={{ 
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 2,
              background: 'white',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Box 
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #2E3B55 0%, #4A5568 100%)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  mb: 2
                }}
              >
                <LockIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  width: '100%', 
                  background: 'rgba(46, 59, 85, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(46, 59, 85, 0.1)'
                }}
              >
                <Typography 
                  variant="h5" 
                  fontWeight="600" 
                  textAlign="center" 
                  color="#2E3B55"
                  sx={{ mb: 2 }}
                >
                  Confirm Your Code
                </Typography>
                
                <Typography 
                  variant="body1" 
                  textAlign="center" 
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  We have sent a verification code to your email.
                  Enter the code below to enable two-factor authentication.
                </Typography>

                <form onSubmit={formik.handleSubmit}>
                  <Stack spacing={3}>
                    <Stack 
                      direction="row" 
                      spacing={{ xs: 1, sm: 2 }} 
                      justifyContent="center"
                      sx={{ mb: 2 }}
                    >
                      {['code1', 'code2', 'code3', 'code4', 'code5', 'code6'].map((field, index) => (
                        <TextField
                          key={field}
                          name={field}
                          value={formik.values[field]}
                          onChange={(e) => handleChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          onBlur={formik.handleBlur}
                          error={formik.touched[field] && Boolean(formik.errors[field])}
                          helperText={formik.touched[field] && formik.errors[field]}
                          inputProps={{ 
                            maxLength: 1, 
                            style: { 
                              textAlign: 'center', 
                              fontSize: '24px',
                              fontWeight: 'bold'
                            } 
                          }}
                          sx={{ 
                            width: { xs: 40, sm: 48 },
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: 'rgba(46, 59, 85, 0.2)',
                                borderRadius: 1.5,
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(46, 59, 85, 0.5)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#2E3B55',
                                borderWidth: 2,
                              },
                            },
                          }}
                          inputRef={(el) => (inputRefs.current[index] = el)}
                        />
                      ))}
                    </Stack>
                    
                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      sx={{
                        py: 1.5,
                        mt: 2,
                        background: 'linear-gradient(135deg, #2E3B55 0%, #4A5568 100%)',
                        borderRadius: 2,
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(46, 59, 85, 0.25)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #3E4B65 0%, #5A6578 100%)',
                          boxShadow: '0 6px 16px rgba(46, 59, 85, 0.3)',
                        }
                      }}
                      disabled={!Object.values(formik.values).every((value) => value !== '')}
                    >
                      CONFIRM
                    </Button>
                    
                    <Typography 
                      variant="body2" 
                      textAlign="center" 
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Didn't receive a code? <Button 
                        variant="text" 
                        sx={{ 
                          p: 0, 
                          minWidth: 'auto', 
                          fontWeight: 'bold',
                          color: '#2E3B55',
                          textTransform: 'none',
                          '&:hover': {
                            background: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Resend
                      </Button>
                    </Typography>
                  </Stack>
                </form>
              </Paper>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Box
        sx={{
          flex: 1,
          background: 'linear-gradient(135deg, #2E3B55 0%, #273041 100%)',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box
          component="img"
          src={SidePic}
          sx={{ height: '100%', width: '100%', objectFit: 'cover' }}
        />
      </Box>
    </Box>
  );
};

export default TwoFactorCode;
